import {
  JobEntity,
  TransactionStatus,
  useSalesTaxTransactionAggregates,
  useSalesTaxTransactions,
  useTransactionFilterOptions,
} from '@commenda/api';
import { LoadingIndicator, useToggle } from '@commenda/kakashi';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { calculateDateRange } from '../../common/filters/DateFilter/utils';
import {
  FilterChangeEvent,
  TransactionFilterState,
} from '../../common/filters/types';
import { isFilterValueEmpty } from '../../common/filters/utils';
import { getIntegrationSyncState } from '../../common/integrationDataSync/utils';
import {
  calculateOverviewDataForTransactions,
  formatNumberWithCommas,
} from '../../utils';

import {
  SortState,
  TransactionListItem,
} from '@commenda/api/new-sales-tax/transactions/types';
import { useNotification } from '../../../hooks/useNotification';
import { useSalesTaxRootFiConnectFlow } from '../../common/useSalesTaxRootFiConnectFlow';
import DataSyncBanner from '../../common/integrationDataSync/DataSyncBanner';
import { useIndirectTaxJobWatcher } from '../../common/jobs';
import { calculatePaginationRangeForCursorBasedPagination } from '../../common/usePaginationRange';
import { useIntegrationSyncAndValidationProgressStatus } from '../../data-review/useIntegrationSyncAndValidationProgressStatus';
import { ImportTransactionsChoiceModal } from '../modal/ImportTransactionsChoiceModal';
import SalesTaxImportTransactionsModal from '../modal/transactions-upload-modal';
import {
  SalesTaxDisableFilterMap,
  SalesTaxFiltersType,
} from '../TransactionFilters/config';
import TransactionsTableSection from '../transactions-table-section';
import TransactionsTabEmptyState from '../TransactionsTabEmptyState';
import TransactionOverview from './TransactionOverview';
import { isFeatureLaunched, FeatureLaunch } from '@commenda/feature-launches';

interface TransactionsTableForModalProps {
  corporationId: number;
  initialFilters?: {
    from_date?: string;
    to_date?: string;
    reporting_date_override_from_date?: string;
    reporting_date_override_to_date?: string;
    transaction_source?: string;
    currency?: string[];
    statuses?: TransactionStatus[];
    destination_states?: string[];
  };
  disableFilters?: SalesTaxDisableFilterMap;
  tableContainerClassName?: string;
  tableWrapperClassName?: string;
  companyId: number;
  salesTaxTestMode?: boolean;
  /** ISO 3166-1 country code for the corporation's incorporation country */
  incorporationCountry?: string;
  /** Whether to use the EU/UK upload flow (simplified flow without template selection) */
  isEuUploadFlow?: boolean;
}

const FilterStateToSalesTaxFilterTypeMap: Record<
  keyof Omit<
    TransactionFilterState,
    'corporationId' | 'currency' | 'search_term'
  >,
  SalesTaxFiltersType
> = {
  from_date: SalesTaxFiltersType.PERIOD,
  to_date: SalesTaxFiltersType.PERIOD,
  reporting_date_override_from_date: SalesTaxFiltersType.REPORTING_PERIOD,
  reporting_date_override_to_date: SalesTaxFiltersType.REPORTING_PERIOD,
  transaction_source: SalesTaxFiltersType.SOURCE_PLATFORMS,
  statuses: SalesTaxFiltersType.STATUSES,
  destination_states: SalesTaxFiltersType.DESTINATION_STATES,
  type: SalesTaxFiltersType.TYPE,
  source_platforms: SalesTaxFiltersType.SOURCE_PLATFORMS,
  marketplaces: SalesTaxFiltersType.MARKETPLACES,
  destination_countries: SalesTaxFiltersType.DESTINATION_COUNTRIES,
};

const TransactionsTableForModal = ({
  corporationId,
  initialFilters = {},
  tableContainerClassName,
  tableWrapperClassName,
  disableFilters = {},
  companyId,
  salesTaxTestMode = false,
  incorporationCountry,
  isEuUploadFlow = false,
}: TransactionsTableForModalProps) => {
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [pageSize] = useState(100);
  const FIRST_PAGE_CURSOR = 'FIRST_PAGE_CURSOR';
  const [hasInitialData, setHasInitialData] = useState(false);
  const [filters, setFilters] = useState<TransactionFilterState>({
    corporationId,
    ...initialFilters,
  });
  const [transactionsListData, setTransactionsListData] = useState<
    TransactionListItem[]
  >([]);
  const [sortState, setSortState] = useState<SortState | null>(null);

  const importTransactionsModal = useToggle();
  const importChoiceModalVisibility = useToggle();

  const { setNotification } = useNotification();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { isIntegrationSyncRecordDataProcessing } =
    useIntegrationSyncAndValidationProgressStatus(corporationId);

  const { isJobInProgress } = useIndirectTaxJobWatcher({
    corporation_id: corporationId,
    entity: JobEntity.INVOICE,
  });

  const {
    integrations,
    handleConnectIntegrationClick,
    connectRootFiIntegrationModal,
  } = useSalesTaxRootFiConnectFlow({
    corporationId,
    setNotification,
  });

  const {
    data: transactionsData,
    error: transactionsError,
    mutate: mutateTransactions,
    isLoading: isLoadingTransactions,
  } = useSalesTaxTransactions({
    filters: { ...filters, search_term: searchTerm },
    cursor: currentCursor === FIRST_PAGE_CURSOR ? null : currentCursor,
    limit: pageSize,
    showTaxBreakdown: true,
    sortState,
  });
  const { aggregates, mutate: mutateAggregates } =
    useSalesTaxTransactionAggregates({
      filters: { ...filters, target_currency: 'USD', search_term: searchTerm },
      sortState,
    });

  // Get filter options for transactions
  const { data: filterOptions } = useTransactionFilterOptions(corporationId);

  useEffect(() => {
    if (transactionsData && !isLoadingTransactions) {
      // Only update if the data has actually changed
      if (
        JSON.stringify(transactionsData.transactions) !==
        JSON.stringify(transactionsListData)
      ) {
        setTransactionsListData(transactionsData.transactions);
      }
    }
  }, [transactionsData, isLoadingTransactions]);

  const overviewData = calculateOverviewDataForTransactions({
    totalSales: aggregates?.totalSales || 0,
    totalTaxCollected: aggregates?.totalTaxCollected || 0,
    totalTransactions: aggregates?.totalTransactions || 0,
  });

  const resetCursor = () => {
    setCurrentCursor(null);
    setCursorHistory([]);
  };

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      resetCursor();
      setSearchTerm(term);
    }, 500),
    []
  );

  const handleSearch = (term: string) => {
    debouncedSearch(term);
  };

  useEffect(() => {
    if (transactionsData?.transactions?.length > 0) {
      setHasInitialData(true);
    }
  }, [transactionsData?.transactions]);

  // Check if there are any filters applied (other than corporationId)
  const hasFiltersApplied = useMemo(() => {
    const hasFiltersOtherThanCorpId = Object.keys(filters || {}).some((key) => {
      const value = filters[key];
      // Skip corporationId and empty values
      if (key === 'corporationId') return false;
      if (!value) return false;
      // Check if it's an array with length > 0 or a non-empty string
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.trim().length > 0;
      return true;
    });

    // Also check searchTerm as it's a filter
    const hasSearchTerm = searchTerm && searchTerm.trim().length > 0;

    return hasFiltersOtherThanCorpId || hasSearchTerm;
  }, [filters, searchTerm]);

  const handleUploadSuccess = async () => {
    resetCursor();
    setTimeout(async () => {
      await Promise.all([mutateTransactions(), mutateAggregates()]);
    }, 1000);
  };

  const handleFilterChange = (event: FilterChangeEvent) => {
    resetCursor();

    if (event.filterId === SalesTaxFiltersType.PERIOD) {
      if (Array.isArray(event.value) && event.value.length === 2) {
        setFilters((prev) => ({
          ...prev,
          from_date: event.value[0],
          to_date: event.value[1],
        }));

        return;
      }

      if (typeof event.value === 'string') {
        const { from_date: fromDate, to_date: toDate } = calculateDateRange(
          event.value
        );
        setFilters((prev) => ({
          ...prev,
          from_date: fromDate,
          to_date: toDate,
        }));
      }
    } else if (event.filterId === SalesTaxFiltersType.REPORTING_PERIOD) {
      if (Array.isArray(event.value) && event.value.length === 2) {
        setFilters((prev) => ({
          ...prev,
          reporting_date_override_from_date: event.value[0],
          reporting_date_override_to_date: event.value[1],
        }));

        return;
      }

      if (typeof event.value === 'string') {
        const { from_date: fromDate, to_date: toDate } = calculateDateRange(
          event.value
        );
        setFilters((prev) => ({
          ...prev,
          reporting_date_override_from_date: fromDate,
          reporting_date_override_to_date: toDate,
        }));
      }
    } else if (!isFilterValueEmpty(event.value)) {
      setFilters((prev) => ({ ...prev, [event.filterId]: event.value }));
    } else {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[event.filterId];
        return newFilters;
      });
    }
  };

  const onFilterClear = (filterId: string) => {
    resetCursor();
    if (filterId === SalesTaxFiltersType.PERIOD) {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters.from_date;
        delete newFilters.to_date;
        return newFilters;
      });
    } else if (filterId === SalesTaxFiltersType.REPORTING_PERIOD) {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters.reporting_date_override_from_date;
        delete newFilters.reporting_date_override_to_date;
        return newFilters;
      });
    } else {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[filterId];
        return newFilters;
      });
    }
  };

  const onClearAll = () => {
    resetCursor();
    setFilters((prev) => {
      const newFilters = { ...prev };
      // dont clear filters that are disabled
      Object.keys(newFilters).forEach((key) => {
        const salesTaxFiltrType =
          FilterStateToSalesTaxFilterTypeMap[
          key as keyof TransactionFilterState
          ];
        if (!(disableFilters[salesTaxFiltrType] ?? false)) {
          delete newFilters[key];
        }
      });
      // keep corporationId filter
      return { ...newFilters, corporationId };
    });
  };

  const handlePageChange = (newCursor: string | null) => {
    if (newCursor === currentCursor) {
      // Going back
      const previousCursor = cursorHistory[cursorHistory.length - 1];
      setCursorHistory((prev) => prev.slice(0, -1));
      setCurrentCursor(previousCursor || null);
    } else {
      // Going forward
      setCursorHistory((prev) => [...prev, currentCursor || FIRST_PAGE_CURSOR]);
      setCurrentCursor(newCursor);
    }
  };

  const handleSortChange = (srtState: SortState) => {
    resetCursor();
    setSortState(srtState);
  };

  const handleImportTransactions = () => {
    if (isFeatureLaunched(FeatureLaunch.INDIRECT_TAX_UPLOAD_TRANSACTION_SERVICE_REQUEST))
      importChoiceModalVisibility.show();
    else
      importTransactionsModal.show();
  };

  const { startIndexOfPage, endIndexOfPage, totalItemsForPage } =
    calculatePaginationRangeForCursorBasedPagination({
      cursorHistoryLength: cursorHistory.length,
      pageSize,
      currentItemsCount: transactionsListData.length,
      totalItems: transactionsData?.totalTransactions || 0,
    });

  const isIntegrationSyncInProgress = getIntegrationSyncState(integrations);

  if (
    !transactionsData ||
    transactionsError ||
    (isLoadingTransactions && !hasInitialData)
  ) {
    return (
      <main className="flex items-center justify-center min-h-screen">
        <LoadingIndicator />
      </main>
    );
  }

  return (
    <>
      {connectRootFiIntegrationModal}
      <ImportTransactionsChoiceModal
        open={importChoiceModalVisibility.visible}
        onOpenChange={(v) => importChoiceModalVisibility.setValue(v)}
        companyId={companyId}
        corporationId={corporationId}
        setNotification={setNotification}
        onSelectImportMyself={() => {
          importChoiceModalVisibility.setValue(false);
          importTransactionsModal.show();
        }}
      />
      <SalesTaxImportTransactionsModal
        isTransactionsModalShown={importTransactionsModal.visible}
        setIsTransactionsModalShown={(val) => {
          importTransactionsModal.setValue(val);
        }}
        companyId={companyId}
        setNotification={setNotification}
        onSuccess={handleUploadSuccess}
        corporationId={corporationId}
        isEuUploadFlow={isEuUploadFlow}
      />

      {!hasInitialData &&
        !transactionsData?.transactions?.length &&
        !hasFiltersApplied ? (
        <TransactionsTabEmptyState
          onImportTransactions={handleImportTransactions}
          onConnectIntegration={handleConnectIntegrationClick}
        />
      ) : (
        <>
          {(isIntegrationSyncInProgress ||
            isJobInProgress(JobEntity.INVOICE) ||
            isIntegrationSyncRecordDataProcessing()) && (
              <div className="px-6">
                <DataSyncBanner
                  corporationId={corporationId}
                  entity={JobEntity.INVOICE}
                  isSyncInProgress={isIntegrationSyncRecordDataProcessing()}
                />
              </div>
            )}

          <main className="flex flex-col py-10 px-6">
            <TransactionOverview overviewData={overviewData} />

            {totalItemsForPage > 0 ? (
              <div className="txt-small-regular text-text-quaternary mt-4 mb-4 px-2">
                Showing {startIndexOfPage}-{endIndexOfPage} of{' '}
                {formatNumberWithCommas(totalItemsForPage)}{' '}
                {totalItemsForPage === 1 ? 'transaction' : 'transactions'}
              </div>
            ) : (
              <div className="txt-small-regular text-text-quaternary mt-4 mb-4 px-2">
                Showing 0-0 of 0 transactions
              </div>
            )}
            <TransactionsTableSection
              tableContainerClassName={tableContainerClassName}
              tableWrapperClassName={tableWrapperClassName}
              corporationId={corporationId}
              pageSize={pageSize}
              data={transactionsListData}
              filters={filters}
              totalTransactions={transactionsData?.totalTransactions}
              nextCursor={transactionsData?.nextCursor}
              currentCursor={currentCursor}
              onPageChange={handlePageChange}
              onSuccess={handleUploadSuccess}
              searchTerm={searchTerm}
              handleSearch={handleSearch}
              sortState={sortState}
              onSortChange={handleSortChange}
              setNotification={setNotification}
              showEstimatedTaxBreakdown
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              onFilterClear={onFilterClear}
              onClearAll={onClearAll}
              salesTaxTestMode={salesTaxTestMode}
              defaultDateColumn="reportingDate"
              storageKeySuffix="filing-table"
              incorporationCountry={incorporationCountry}
              isEuUploadFlow={isEuUploadFlow}
            />
          </main>
        </>
      )}
    </>
  );
};

export default TransactionsTableForModal;
